import React, { FC } from 'react'
import { Button, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { hasOmsOrderAccess } from '../orderAccessUtils'

const PackageActionIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M12 2L4 6.5V17.5L12 22L20 17.5V6.5L12 2Z'
      stroke='#1F2937'
      strokeWidth='1.8'
      strokeLinejoin='round'
    />
    <path d='M4 6.5L12 11L20 6.5' stroke='#1F2937' strokeWidth='1.8' strokeLinejoin='round' />
    <path d='M12 11V22' stroke='#1F2937' strokeWidth='1.8' />
  </svg>
)

const InvoiceActionIcon: FC = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z' />
    <path d='M14 2v4a2 2 0 0 0 2 2h4' />
    <path d='M8 13h4' />
    <path d='M8 17h8' />
  </svg>
)

const EditOrderActionIcon: FC = () => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M12 20h9' />
    <path d='M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z' />
  </svg>
)

const actionButtonStyle: React.CSSProperties = { height: '28px', width: '28px' }

export type OrderRowActionsCellProps = {
  row: any
  omsPlatformId: number | null
  invoiceDownloadLoading: boolean
  shouldShowPackageAction: (row: any) => boolean
  isInvoiceEligibleOrder: (row: any) => boolean
  onDownloadInvoice: (row: any) => void
  onOpenPackageModal: (row: any) => void
  onEditOrder: (effectiveOrderId: string | number) => void
  onOpenOmsPackageModal: (row: any) => void
  onOpenDirectShipmentModal: (row: any) => void
}

const OrderRowActionsCell: FC<OrderRowActionsCellProps> = ({
  row,
  omsPlatformId,
  invoiceDownloadLoading,
  shouldShowPackageAction,
  isInvoiceEligibleOrder,
  onDownloadInvoice,
  onOpenPackageModal,
  onEditOrder,
  onOpenOmsPackageModal,
  onOpenDirectShipmentModal,
}) => {
  const showPackageAction = shouldShowPackageAction(row)
  const showInvoiceAction = isInvoiceEligibleOrder(row)
  const { hasAccess, effectiveOrderId } = hasOmsOrderAccess(row, omsPlatformId)
  const showEditAction = Boolean(hasAccess && effectiveOrderId)
  const showKebabMenu = Boolean(hasAccess && effectiveOrderId)

  if (!showPackageAction && !showInvoiceAction && !showEditAction && !showKebabMenu) {
    return null
  }

  return (
    <div className='w-100 d-flex justify-content-center align-items-center gap-2'>
      {showInvoiceAction ? (
        <OverlayTrigger placement='top' overlay={<Tooltip>Download Invoice</Tooltip>}>
          <Button
            variant='link'
            className='p-0 d-inline-flex align-items-center justify-content-center text-primary'
            style={actionButtonStyle}
            disabled={invoiceDownloadLoading}
            onClick={() => onDownloadInvoice(row)}
          >
            <InvoiceActionIcon />
          </Button>
        </OverlayTrigger>
      ) : null}

      {showPackageAction ? (
        <OverlayTrigger placement='top' overlay={<Tooltip>Manage Package</Tooltip>}>
          <Button
            variant='link'
            className='p-0 d-inline-flex align-items-center justify-content-center'
            style={actionButtonStyle}
            onClick={() => onOpenPackageModal(row)}
          >
            <PackageActionIcon />
          </Button>
        </OverlayTrigger>
      ) : null}

      {showEditAction ? (
        <OverlayTrigger placement='top' overlay={<Tooltip>Edit Order</Tooltip>}>
          <Button
            variant='link'
            className='p-0 d-inline-flex align-items-center justify-content-center text-primary'
            style={actionButtonStyle}
            onClick={() => onEditOrder(effectiveOrderId!)}
          >
            <EditOrderActionIcon />
          </Button>
        </OverlayTrigger>
      ) : null}

      {showKebabMenu ? (
        <Dropdown align='end'>
          <Dropdown.Toggle
            variant='link'
            className='p-0 d-inline-flex align-items-center justify-content-center'
            style={{ ...actionButtonStyle, border: 'none', boxShadow: 'none' }}
            bsPrefix='btn'
          >
            <svg width='16' height='16' viewBox='0 0 24 24' fill='#555'>
              <circle cx='12' cy='5' r='2' />
              <circle cx='12' cy='12' r='2' />
              <circle cx='12' cy='19' r='2' />
            </svg>
          </Dropdown.Toggle>
          <Dropdown.Menu style={{ fontSize: '13px', minWidth: '170px' }}>
            <Dropdown.Item onClick={() => onOpenOmsPackageModal({ ...row, id: effectiveOrderId })}>
              📦 Manage Packages
            </Dropdown.Item>
            <Dropdown.Item onClick={() => onOpenDirectShipmentModal({ ...row, id: effectiveOrderId })}>
              🚚 Manage Shipments
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => {}} className='text-danger'>
              🗑️ Delete Order
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      ) : null}
    </div>
  )
}

export default OrderRowActionsCell
